/**
 * Verificador dos arquivos de um plugin web (docs/spec/web-plugins.md, "Criar com IA").
 *
 * Confere o que faria a ficha falhar dentro do RPG Desk (arquivo que falta, script de fora, caminho
 * de campo inválido) e avisa do que o sandbox bloqueia em silêncio (`alert`, `localStorage`,
 * internet). É feito de expressões regulares, não de um analisador completo: o objetivo é pegar
 * os erros comuns com mensagens claras, não provar que o código está certo.
 */
import { MIME_TYPES, PACKAGE_LIMITS, SDK_CDN_BASE, extensionOf, isHtmlPath, isTextPath, mimeOf } from './constants.js';
import { fieldPathProblem } from './fields.js';
import { lineAt, type Issue } from './issues.js';
import type { WebManifest } from './manifest.js';
import { MESSAGES, type Lang } from './messages.js';

const decoder = new TextDecoder('utf-8');
export const decodeText = (bytes: Uint8Array): string => decoder.decode(bytes).replace(/^﻿/, '');

/** O endereço aponta para o SDK (o público, no jsDelivr, ou a cópia que o RPG Desk serve em `_sdk/`)? */
export function isSdkUrl(url: string): boolean {
  return url.startsWith(SDK_CDN_BASE) || /(^|\/)_sdk\/\d+\/[\w.-]+$/.test(url.split('?')[0]!);
}

/** Script que carrega o SDK: o público, o do RPG Desk, ou uma cópia dentro do pacote. */
const isSdkScript = (url: string): boolean => isSdkUrl(url) || /(^|\/)rpgdesk(\.min)?\.js$/.test(url.split('?')[0]!);

/** Caminho do pacote a partir do arquivo que o cita, ou null se sai do pacote (`../../x`). */
export function resolveRef(from: string, ref: string): string | null {
  const clean = ref.split('#')[0]!.split('?')[0]!;
  if (clean === '') return null;
  const base = clean.startsWith('/') ? [] : from.split('/').slice(0, -1);
  const parts = [...base];
  for (const part of clean.split('/')) {
    if (part === '' || part === '.') continue;
    if (part === '..') {
      if (parts.length === 0) return null;
      parts.pop();
    } else {
      let decoded = part;
      try {
        decoded = decodeURIComponent(part);
      } catch {
        // fica como está
      }
      parts.push(decoded);
    }
  }
  return parts.join('/');
}

type Ref = { url: string; index: number; tag: string };

const isExternal = (url: string) => /^(https?:)?\/\//i.test(url);
const isIgnorable = (url: string) => /^(data:|blob:|#|mailto:|tel:|javascript:|about:)/i.test(url) || url.trim() === '' || /^\{\{|\$\{/.test(url);

const ATTR = /\s([a-zA-Z:_-]+)\s*=\s*("([^"]*)"|'([^']*)'|([^\s"'>]+))/g;

function attributes(tag: string): Map<string, { value: string; index: number }> {
  const out = new Map<string, { value: string; index: number }>();
  for (const m of tag.matchAll(ATTR)) {
    const name = m[1]!.toLowerCase();
    if (!out.has(name)) out.set(name, { value: m[3] ?? m[4] ?? m[5] ?? '', index: m.index ?? 0 });
  }
  return out;
}

/** Referências de um HTML (script, link, img, mídia, links). */
function htmlRefs(html: string): Ref[] {
  const refs: Ref[] = [];
  for (const m of html.matchAll(/<(script|link|img|source|audio|video|track|a|image|use|embed|object|input)\b([^>]*)>/gi)) {
    const tag = m[1]!.toLowerCase();
    const attrs = attributes(m[2]!);
    for (const name of ['src', 'href', 'xlink:href', 'poster', 'data']) {
      const a = attrs.get(name);
      if (!a) continue;
      if (tag === 'link' && name === 'href') {
        const rel = (attrs.get('rel')?.value ?? '').toLowerCase();
        if (/\b(preconnect|dns-prefetch)\b/.test(rel)) {
          refs.push({ url: a.value, index: (m.index ?? 0) + a.index, tag: 'preconnect' });
          continue;
        }
      }
      refs.push({ url: a.value, index: (m.index ?? 0) + a.index, tag });
    }
    const srcset = attrs.get('srcset');
    if (srcset) for (const part of srcset.value.split(',')) refs.push({ url: part.trim().split(/\s+/)[0] ?? '', index: (m.index ?? 0) + srcset.index, tag });
  }
  return refs;
}

/** Referências de um CSS (`url(...)` e `@import`). */
function cssRefs(css: string, offset = 0): Ref[] {
  const refs: Ref[] = [];
  for (const m of css.matchAll(/@import\s+(?:url\(\s*)?["']?([^"')\s;]+)["']?\s*\)?/gi)) refs.push({ url: m[1]!, index: offset + (m.index ?? 0), tag: '@import' });
  for (const m of css.matchAll(/url\(\s*(["']?)([^"')]+)\1\s*\)/gi)) {
    const before = css.slice(Math.max(0, (m.index ?? 0) - 8), m.index ?? 0);
    if (/@import\s*$/i.test(before)) continue;
    refs.push({ url: m[2]!.trim(), index: offset + (m.index ?? 0), tag: 'url()' });
  }
  return refs;
}

/** Trechos de script embutidos num HTML (sem os de dados, como o do manifesto). */
function inlineScripts(html: string): { code: string; index: number }[] {
  const out: { code: string; index: number }[] = [];
  for (const m of html.matchAll(/<script\b([^>]*)>([\s\S]*?)<\/script\s*>/gi)) {
    const type = (attributes(m[1]!).get('type')?.value ?? '').toLowerCase();
    if (type && !/javascript|module|ecmascript/.test(type)) continue;
    const start = (m.index ?? 0) + m[0].indexOf('>') + 1;
    out.push({ code: m[2]!, index: start });
  }
  return out;
}

function inlineStyles(html: string): { css: string; index: number }[] {
  const out: { css: string; index: number }[] = [];
  for (const m of html.matchAll(/<style\b[^>]*>([\s\S]*?)<\/style\s*>/gi)) out.push({ css: m[1]!, index: (m.index ?? 0) + m[0].indexOf('>') + 1 });
  return out;
}

/** `text`: a chave dos textos em `MESSAGES[lang].js` ([problema, conserto]). */
type JsRule = { code: string; re: RegExp; level: 'error' | 'warning'; text: string };

const JS_RULES: JsRule[] = [
  {
    code: 'js-import-external',
    re: /(?:\bimport\s*(?:[\w*{}\s,]+from\s*)?|\bimport\s*\(\s*)["'`](?:https?:)?\/\/[^"'`]+["'`]/g,
    level: 'error',
    text: 'importExternal',
  },
  {
    code: 'js-dialog',
    re: /(?<![\w$.])(?:window\.)?(alert|confirm|prompt)\s*\(/g,
    level: 'warning',
    text: 'dialog',
  },
  {
    code: 'js-storage',
    re: /\b(localStorage|sessionStorage|indexedDB|document\.cookie)\b/g,
    level: 'warning',
    text: 'storage',
  },
  {
    code: 'js-network',
    re: /\bfetch\s*\(\s*["'`](?:https?:)?\/\/|\bnew\s+(WebSocket|EventSource)\s*\(|\bXMLHttpRequest\b|\bnavigator\.sendBeacon\s*\(/g,
    level: 'warning',
    text: 'network',
  },
  {
    code: 'js-window-open',
    re: /\bwindow\.open\s*\(/g,
    level: 'warning',
    text: 'windowOpen',
  },
  {
    code: 'js-navigation',
    re: /(?<![\w$.])(?<!\b(?:const|let|var)\s+)(?:(?:window|document|top|parent|self)\.)?location(?:\.href)?\s*=(?!=)|(?<![\w$.])(?:(?:window|document|top|parent|self)\.)?location\.(assign|replace)\s*\(/g,
    level: 'warning',
    text: 'navigation',
  },
  {
    code: 'js-webrtc',
    re: /\bRTCPeerConnection\b/g,
    level: 'warning',
    text: 'webrtc',
  },
];

/** Linhas de comentário (// ou * no começo) não contam para as regras de JS. */
function commentRanges(code: string): [number, number][] {
  const out: [number, number][] = [];
  for (const m of code.matchAll(/\/\*[\s\S]*?\*\//g)) out.push([m.index ?? 0, (m.index ?? 0) + m[0].length]);
  for (const m of code.matchAll(/(^|[^:"'`\\])\/\/[^\n]*/g)) out.push([(m.index ?? 0) + m[1]!.length, (m.index ?? 0) + m[0].length]);
  return out;
}

/**
 * Código de biblioteca (jQuery, Alpine…): um arquivo numa pasta `vendor/`, ou um código que começa
 * com o comentário de licença `/*!`. Os avisos não valem para ele (o `$.ajax` do jQuery cita
 * `XMLHttpRequest` sem a ficha usar a rede); os erros, sim.
 */
const isVendorPath = (path: string): boolean => /(^|\/)vendor\//.test(path);
const isLibraryCode = (code: string): boolean => /^\s*\/\*!/.test(code);

function scanJs(code: string, file: string, text: string, offset: number, issues: Issue[], lang: Lang, library = false): void {
  const comments = commentRanges(code);
  const inComment = (i: number) => comments.some(([a, b]) => i >= a && i < b);
  for (const rule of JS_RULES) {
    if (library && rule.level === 'warning') continue;
    const seen = new Set<number>();
    for (const m of code.matchAll(rule.re)) {
      const at = m.index ?? 0;
      if (inComment(at)) continue;
      const line = lineAt(text, offset + at);
      if (seen.has(line)) continue;
      seen.add(line);
      const [message, fix] = MESSAGES[lang].js[rule.text]!;
      issues.push({ level: rule.level, code: rule.code, message, fix, file, line });
    }
  }
}

/** Atributos do SDK com caminho de campo; `data-roll`/`data-label` têm `{caminho}` no texto. */
function scanBindings(html: string, file: string, issues: Issue[], lang: Lang): void {
  const M = MESSAGES[lang].files;
  for (const m of html.matchAll(/\sdata-(field|text|image|list|list-add)\s*=\s*("([^"]*)"|'([^']*)')/gi)) {
    const path = m[3] ?? m[4] ?? '';
    const problem = fieldPathProblem(path, lang);
    if (problem) {
      issues.push({ level: 'error', code: 'binding-path', message: `data-${m[1]!.toLowerCase()}: ${problem}`, fix: M.bindingFix, file, line: lineAt(html, m.index ?? 0) });
    }
  }
  for (const m of html.matchAll(/\sdata-(roll|label|(?:pt|en)-label)\s*=\s*("([^"]*)"|'([^']*)')/gi)) {
    const value = m[3] ?? m[4] ?? '';
    for (const p of value.matchAll(/\{([^{}]*)\}/g)) {
      const problem = fieldPathProblem(p[1]!.trim(), lang);
      if (problem) {
        issues.push({ level: 'error', code: 'binding-placeholder', message: M.placeholder(m[1]!.toLowerCase(), p[1]!, problem), fix: M.placeholderFix, file, line: lineAt(html, m.index ?? 0) });
      }
    }
  }
}

/** Campos e tags sem texto dentro: `data-en` neles não tem o que trocar. */
const NO_TEXT_TAGS = new Set(['input', 'textarea', 'select', 'img', 'br', 'hr', 'area', 'embed', 'source', 'track', 'wbr', 'iframe', 'video', 'audio', 'canvas', 'object', 'meta', 'link', 'base', 'col']);
/** Tags que o HTML fecha sozinho quando vem uma destas logo depois (`<option>A<option>B`). */
const CLOSED_BY: Record<string, string[]> = {
  option: ['option', 'optgroup'],
  li: ['li'],
  dt: ['dt', 'dd'],
  dd: ['dt', 'dd'],
  tr: ['tr', 'tbody', 'tfoot'],
  td: ['td', 'th', 'tr'],
  th: ['td', 'th', 'tr'],
  p: ['p', 'div', 'ul', 'ol', 'table', 'section', 'header', 'footer', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
};

/** Comentários, scripts e estilos viram espaço (as linhas continuam no lugar). */
const maskCode = (html: string): string => html.replace(/<!--[\s\S]*?-->|<script\b[\s\S]*?<\/script\s*>|<style\b[\s\S]*?<\/style\s*>/gi, (m) => m.replace(/[^\n]/g, ' '));

/**
 * `data-en`/`data-pt` onde o SDK não troca o texto (docs/spec/web-plugins.md, "As duas línguas"):
 * num campo ou numa tag vazia, junto com `data-text`/`data-field`, ou num elemento com outros
 * dentro (o SDK não apaga o `<input>` de um `<label>`).
 */
function scanTranslations(html: string, file: string, issues: Issue[], lang: Lang): void {
  const M = MESSAGES[lang].files;
  const text = maskCode(html);
  // o valor entre aspas pode ter `>` (`data-roll="5d10 > 7"`)
  for (const m of text.matchAll(/<([a-zA-Z][\w-]*)((?:[^>"']|"[^"]*"|'[^']*')*)>/g)) {
    const tag = m[1]!.toLowerCase();
    const attrs = attributes(m[2]!);
    const found = (['en', 'pt'] as const).map((l) => `data-${l}`).find((a) => attrs.get(a)?.value);
    if (!found) continue;
    const line = lineAt(html, (m.index ?? 0) + attrs.get(found)!.index);
    const issue = (message: string, fix: string) => issues.push({ level: 'warning', code: 'binding-translation', message, fix, file, line });
    if (NO_TEXT_TAGS.has(tag)) {
      issue(M.translationField(found, tag), M.translationFieldFix);
      continue;
    }
    if (attrs.has('data-text') || attrs.has('data-field')) {
      issue(M.translationBound(found), M.translationBoundFix(found));
      continue;
    }
    // a primeira tag depois desta: o fechamento (dela, ou do pai) diz que só há texto dentro
    const next = /<(\/?)([a-zA-Z][\w-]*)/g;
    next.lastIndex = (m.index ?? 0) + m[0].length;
    const after = next.exec(text);
    if (!after || after[1] === '/') continue;
    if (CLOSED_BY[tag]?.includes(after[2]!.toLowerCase())) continue;
    issue(M.translationChildren(found, tag), M.translationChildrenFix(found));
  }
}

function checkRefs(refs: Ref[], file: string, text: string, files: ReadonlyMap<string, Uint8Array>, lowerIndex: ReadonlyMap<string, string>, issues: Issue[], lang: Lang): void {
  const M = MESSAGES[lang].files;
  for (const ref of refs) {
    const url = ref.url.trim();
    if (isIgnorable(url)) continue;
    const line = lineAt(text, ref.index);
    if (isExternal(url)) {
      if (isSdkUrl(url)) continue;
      if (ref.tag === 'a') {
        issues.push({ level: 'warning', code: 'link-external', message: M.linkExternal(url), fix: M.linkExternalFix, file, line });
      } else if (ref.tag === 'script') {
        issues.push({ level: 'error', code: 'external-script', message: M.externalScript(url), fix: M.externalScriptFix, file, line });
      } else if (ref.tag === 'preconnect') {
        issues.push({ level: 'warning', code: 'external-resource', message: M.preconnect(url), fix: M.preconnectFix, file, line });
      } else {
        issues.push({
          level: 'warning',
          code: 'external-resource',
          message: M.externalResource(url),
          fix: ref.tag === '@import' || /fonts\.googleapis|fonts\.gstatic/.test(url) ? M.externalFontFix : M.externalResourceFix,
          file,
          line,
        });
      }
      continue;
    }
    // a cópia do SDK que o RPG Desk serve fica fora do pacote, em `_sdk/`
    if (isSdkUrl(url)) continue;
    const target = resolveRef(file, url);
    if (target === null) {
      issues.push({ level: 'error', code: 'ref-outside', message: M.refOutside(url), fix: M.refOutsideFix, file, line });
      continue;
    }
    if (files.has(target)) {
      if (ref.tag === 'a' && isHtmlPath(target)) {
        issues.push({ level: 'warning', code: 'link-page', message: M.linkPage(url), fix: M.linkPageFix, file, line });
      }
      continue;
    }
    const sameButCase = lowerIndex.get(target.toLowerCase());
    if (sameButCase) {
      issues.push({ level: 'error', code: 'ref-case', message: M.refCase(url, sameButCase), fix: M.refCaseFix(sameButCase), file, line });
    } else if (ref.tag !== 'a') {
      issues.push({ level: 'error', code: 'ref-missing', message: M.refMissing(url), fix: M.refMissingFix, file, line });
    }
  }
}

/**
 * Confere os arquivos do pacote contra o manifesto já validado. `files`: caminho → bytes, como
 * ficam no .zip (o arquivo único vira `index.html`).
 */
export function verifyFiles(manifest: WebManifest, files: ReadonlyMap<string, Uint8Array>, opts: { packageBytes: number; lang?: Lang }): Issue[] {
  const lang = opts.lang ?? 'pt';
  const M = MESSAGES[lang].files;
  const issues: Issue[] = [];
  if (opts.packageBytes > PACKAGE_LIMITS.maxBytes) {
    issues.push({ level: 'error', code: 'package-size', message: M.packageSize((opts.packageBytes / 1024 / 1024).toFixed(1), PACKAGE_LIMITS.maxBytes / 1024 / 1024), fix: M.packageSizeFix });
  }
  if (files.size > PACKAGE_LIMITS.maxFiles) {
    issues.push({ level: 'error', code: 'package-files', message: M.packageFiles(files.size, PACKAGE_LIMITS.maxFiles) });
  }
  const lowerIndex = new Map<string, string>();
  for (const path of files.keys()) {
    lowerIndex.set(path.toLowerCase(), path);
    if (path.startsWith('/') || path.includes('\\') || path.split('/').some((p) => p === '..' || p === '.')) {
      issues.push({ level: 'error', code: 'path-invalid', message: M.pathInvalid(path), fix: M.pathInvalidFix, file: path });
      continue;
    }
    if (!mimeOf(path)) {
      issues.push({ level: 'error', code: 'file-type', message: M.fileType(path, extensionOf(path)), fix: M.fileTypeFix(Object.keys(MIME_TYPES).map((e) => `.${e}`).join(' ')), file: path });
    }
  }

  const entries = [...manifest.sheets, ...manifest.tables].map((d) => d.entry);
  for (const entry of new Set(entries)) {
    if (files.has(entry)) continue;
    const other = lowerIndex.get(entry.toLowerCase());
    issues.push(
      other
        ? { level: 'error', code: 'entry-case', message: M.entryCase(entry, other), fix: M.entryCaseFix(other), file: 'manifest.json' }
        : { level: 'error', code: 'entry-missing', message: M.entryMissing(entry), fix: M.entryMissingFix, file: 'manifest.json' },
    );
  }
  if (manifest.icon && !files.has(manifest.icon)) {
    issues.push({ level: 'error', code: 'icon-missing', message: M.iconMissing(manifest.icon), file: 'manifest.json' });
  }

  for (const [path, bytes] of files) {
    if (!isTextPath(path) || path === 'manifest.json') continue;
    const text = decodeText(bytes);
    const ext = extensionOf(path);
    if (ext === 'html' || ext === 'htm') {
      checkRefs(htmlRefs(text), path, text, files, lowerIndex, issues, lang);
      for (const style of inlineStyles(text)) checkRefs(cssRefs(style.css, style.index), path, text, files, lowerIndex, issues, lang);
      // uma biblioteca colada num <script> tem `<form>` e afins em textos dela, que não são da página
      const libraries: [number, number][] = [];
      for (const script of inlineScripts(text)) {
        const library = isLibraryCode(script.code);
        if (library) libraries.push([script.index, script.index + script.code.length]);
        scanJs(script.code, path, text, script.index, issues, lang, library);
      }
      const inLibrary = (i: number) => libraries.some(([a, b]) => i >= a && i < b);
      scanBindings(text, path, issues, lang);
      scanTranslations(text, path, issues, lang);
      for (const m of text.matchAll(/<form\b/gi)) {
        if (inLibrary(m.index ?? 0)) continue;
        issues.push({ level: 'warning', code: 'html-form', message: M.form, fix: M.formFix, file: path, line: lineAt(text, m.index ?? 0) });
      }
      for (const m of text.matchAll(/<base\b/gi)) {
        if (inLibrary(m.index ?? 0)) continue;
        issues.push({ level: 'warning', code: 'html-base', message: M.base, fix: M.baseFix, file: path, line: lineAt(text, m.index ?? 0) });
      }
      for (const m of text.matchAll(/<iframe\b/gi)) {
        if (inLibrary(m.index ?? 0)) continue;
        issues.push({ level: 'warning', code: 'html-iframe', message: M.iframe, fix: M.iframeFix, file: path, line: lineAt(text, m.index ?? 0) });
      }
      if (entries.includes(path) && !htmlRefs(text).some((r) => r.tag === 'script' && isSdkScript(r.url.trim())) && !/@rpgdesk\/sdk/.test(text)) {
        issues.push({ level: 'warning', code: 'sdk-missing', message: M.sdkMissing, fix: M.sdkMissingFix(`${SDK_CDN_BASE}rpgdesk.js`), file: path });
      }
    } else if (ext === 'css') {
      checkRefs(cssRefs(text), path, text, files, lowerIndex, issues, lang);
    } else if (ext === 'js' || ext === 'mjs') {
      scanJs(text, path, text, 0, issues, lang, isVendorPath(path) || isLibraryCode(text));
    }
  }
  return issues;
}
